import { useEffect, useMemo, useRef, useState } from 'react';
import {
  isAlgoliaConfigured,
  searchCalibreFacetValues,
  searchReferencesByCalibre,
  searchReferencesByDescription,
} from '../../services/algolia';
import type { CalibreFacetValue } from '../../services/algolia';
import {
  getWatchBrands,
  getWatchReference,
  getImageUrl,
  getCustomCalibres,
} from '../../services/firebase';
import { normalizeCalibreKey } from '../../services/calibreResolution';
import type {
  WatchBrand,
  WatchReference,
  WatchReferenceAlgoliaHit,
} from '../../types';
import { ReferenceEditor } from './ReferenceEditor';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';

// ── Thumbnail ─────────────────────────────────────────────────────────────────
//
// Algolia hits store either a public URL or a Storage path in `imageUrl`;
// resolve the latter to a download URL (mirrors ReferenceBrowser's
// SearchResultCard). Kept tiny — this is a 40px list thumbnail.
function RefThumb({ hit }: { hit: WatchReferenceAlgoliaHit }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!hit.imageUrl) {
      setUrl(null);
      return;
    }
    const needsResolving =
      !hit.imageUrl.startsWith('http') ||
      (hit.imageUrl.includes('firebasestorage.googleapis.com') &&
        !hit.imageUrl.includes('token='));
    if (!needsResolving) {
      setUrl(hit.imageUrl);
      return;
    }
    let storagePath = hit.imageUrl;
    if (hit.imageUrl.includes('firebasestorage.googleapis.com')) {
      const match = hit.imageUrl.match(/\/o\/(.+?)(\?|$)/);
      if (match) storagePath = decodeURIComponent(match[1]);
    }
    getImageUrl(storagePath)
      .then((u) => !cancelled && setUrl(u))
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [hit.imageUrl]);

  return (
    <div className="w-10 h-10 shrink-0 rounded bg-gray-100 overflow-hidden">
      {url ? (
        <img src={url} alt={hit.reference} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-300">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

// ── CrossBrandCalibreSearch ───────────────────────────────────────────────────
//
// Cross-brand cousin of the brand-scoped calibre dropdown on ReferenceBrowser
// (PR #267): pick any calibre string index-wide and browse every reference that
// carries it, across all brands. Entirely Algolia-driven — the calibre picker
// runs a query-scoped facet search (see searchCalibreFacetValues) and the
// matching-refs list filters on the `calibre` facet, so there's no full-catalogue
// scan and no new backend. Clicking a row opens that reference in the standard
// ReferenceEditor (this component self-hosts it, mirroring CuratedWatches).

const PAGE_SIZE = 50;
const PICKER_DEBOUNCE_MS = 250;
// The picker fetches the COMPLETE matching facet set (see searchCalibreFacetValues);
// this only bounds how many we render. At/below the cap the dropdown shows every
// candidate — no hidden long-tail variants; above it we show the first N and hint
// that more exist. 50 comfortably covers a typical query's grade/variant spread
// while keeping the list scannable.
const PICKER_DISPLAY_CAP = 50;
// Description-only matches are fetched in a single query (they're not paginated
// like the primary calibre-facet list). For a specific calibre-grade string the
// set is typically small; this cap keeps it a single cheap call and the UI hints
// when the live index holds more than we render.
const DESCRIPTION_MATCH_CAP = 100;

export function CrossBrandCalibreSearch() {
  const configured = isAlgoliaConfigured();

  // brandId → display name, loaded once so cross-brand rows can name their brand
  // without a per-row fetch. Also the source of the full WatchBrand needed to
  // open the editor.
  const [brandsById, setBrandsById] = useState<Map<string, WatchBrand>>(new Map());

  // Tier resolver: normalized calibre name/alias → (tierId → tier name). Built
  // ONCE from the full custom_calibres collection (a single query), then reused
  // for every row across every results page — O(1) per row, no per-row fetch.
  // Only tier-bearing calibres are inserted, so a plain (tier-less) calibre that
  // shares a name never shadows one that has tiers. Mirrors the case-insensitive
  // canonical/alias resolver in coDevelopedCalibreBackfill.
  const [tierNamesByCalibre, setTierNamesByCalibre] = useState<
    Map<string, Map<string, string>>
  >(new Map());

  // Calibre picker
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<CalibreFacetValue[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Selected calibre + its matching references (paginated)
  const [selectedCalibre, setSelectedCalibre] = useState<CalibreFacetValue | null>(null);
  const [hits, setHits] = useState<WatchReferenceAlgoliaHit[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // "Also search descriptions" — off by default so the picker value alone drives
  // results. When on, we additionally surface refs whose DESCRIPTION carries the
  // calibre string (grade/variant often lives only there). These are loaded in a
  // single query, deduped against the exact-calibre set, and shown with a chip.
  const [alsoSearchDescriptions, setAlsoSearchDescriptions] = useState(false);
  const [descriptionHits, setDescriptionHits] = useState<WatchReferenceAlgoliaHit[]>([]);
  const [descriptionTruncated, setDescriptionTruncated] = useState(false);
  const [descriptionLoading, setDescriptionLoading] = useState(false);

  // Editor view
  const [editorTarget, setEditorTarget] = useState<
    { reference: WatchReference; brand: WatchBrand } | null
  >(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  // Bridge to the editor's unsaved-changes guard so the breadcrumb "Calibre
  // Search" link routes through the same confirm dialog while the edit is dirty.
  const editorExitGuardRef = useRef<((proceed: () => void) => void) | null>(
    null,
  );

  // ── Load brands once ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getWatchBrands()
      .then((brands) => {
        if (cancelled) return;
        setBrandsById(new Map(brands.map((b) => [b.id, b])));
      })
      .catch((err) => console.error('Failed to load brands:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Load calibre tiers once → build the name→tier resolver ─────────────────────
  useEffect(() => {
    let cancelled = false;
    getCustomCalibres()
      .then((calibres) => {
        if (cancelled) return;
        const map = new Map<string, Map<string, string>>();
        for (const cal of calibres) {
          const tiers = cal.data?.tiers;
          if (!tiers || tiers.length === 0) continue;
          const tierMap = new Map<string, string>();
          for (const t of tiers) tierMap.set(t.id, t.name);
          for (const key of [cal.name, ...(cal.alsoKnownAs ?? [])]) {
            const norm = normalizeCalibreKey(key);
            if (norm) map.set(norm, tierMap);
          }
        }
        setTierNamesByCalibre(map);
      })
      .catch((err) => console.error('Failed to load calibres for tier resolve:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Calibre picker (debounced query-scoped facet search) ──────────────────────
  useEffect(() => {
    if (!configured) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPickerLoading(true);
    debounceRef.current = setTimeout(() => {
      // No maxValues — take the complete matching set; the dropdown itself caps
      // how many rows it renders (PICKER_DISPLAY_CAP) and hints when it truncates.
      searchCalibreFacetValues(query)
        .then((res) => {
          setOptions(res);
          setPickerOpen(true);
        })
        .catch((err) => {
          console.error('Calibre facet search failed:', err);
          setOptions([]);
        })
        .finally(() => setPickerLoading(false));
    }, PICKER_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, configured]);

  // ── Load matching references when a calibre is selected ───────────────────────
  useEffect(() => {
    if (!selectedCalibre) {
      setHits([]);
      setTotalHits(0);
      setHasMore(false);
      return;
    }
    let cancelled = false;
    setListLoading(true);
    setPage(0);
    searchReferencesByCalibre(selectedCalibre.value, { page: 0, hitsPerPage: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setHits(res.hits);
        setTotalHits(res.nbHits);
        setHasMore(res.page + 1 < res.nbPages);
      })
      .catch((err) => {
        console.error('Failed to load references for calibre:', err);
        toast.error('Failed to load references');
      })
      .finally(() => !cancelled && setListLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedCalibre]);

  // ── Load description-only matches when the toggle is on ───────────────────────
  // Separate from the paginated calibre-facet list: fetched once (capped), then
  // deduped so a ref whose own `calibre` equals the picker value is dropped — it
  // already belongs to the exact-calibre set (precedence: calibre match wins, no
  // chip). What remains genuinely matched only via description.
  useEffect(() => {
    if (!selectedCalibre || !alsoSearchDescriptions) {
      setDescriptionHits([]);
      setDescriptionTruncated(false);
      return;
    }
    let cancelled = false;
    const value = selectedCalibre.value;
    setDescriptionLoading(true);
    searchReferencesByDescription(value, { page: 0, hitsPerPage: DESCRIPTION_MATCH_CAP })
      .then((res) => {
        if (cancelled) return;
        setDescriptionHits(res.hits.filter((h) => h.calibre !== value));
        setDescriptionTruncated(res.nbPages > 1);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load description matches:', err);
        toast.error('Failed to search descriptions');
      })
      .finally(() => !cancelled && setDescriptionLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedCalibre, alsoSearchDescriptions]);

  const loadMore = async () => {
    if (!selectedCalibre || !hasMore || loadingMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const res = await searchReferencesByCalibre(selectedCalibre.value, {
        page: nextPage,
        hitsPerPage: PAGE_SIZE,
      });
      setHits((prev) => [...prev, ...res.hits]);
      setPage(nextPage);
      setHasMore(res.page + 1 < res.nbPages);
    } catch (err) {
      console.error('Failed to load more references:', err);
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const selectCalibre = (opt: CalibreFacetValue) => {
    setSelectedCalibre(opt);
    setQuery(opt.value);
    setPickerOpen(false);
  };

  const clearCalibre = () => {
    setSelectedCalibre(null);
    setQuery('');
    setHits([]);
    setTotalHits(0);
    setDescriptionHits([]);
    setDescriptionTruncated(false);
  };

  // ── Open a reference in the editor ────────────────────────────────────────────
  const openReference = async (hit: WatchReferenceAlgoliaHit) => {
    const brand = brandsById.get(hit.brandId);
    if (!brand) {
      toast.error('Brand not found for this reference');
      return;
    }
    setOpeningId(hit.objectID);
    try {
      const reference = await getWatchReference(hit.brandId, hit.referenceId);
      if (!reference) {
        toast.error('Reference not found');
        return;
      }
      setEditorTarget({ reference, brand });
    } catch (err) {
      console.error('Failed to open reference:', err);
      toast.error('Failed to open reference');
    } finally {
      setOpeningId(null);
    }
  };

  const brandName = useMemo(
    () => (id: string) => {
      const b = brandsById.get(id);
      return b?.displayName || b?.name || id;
    },
    [brandsById],
  );

  // Resolve a row's tier NAME from its calibre string + calibreTierId, using the
  // prebuilt resolver map. Returns null when the ref carries no tier, its calibre
  // isn't tier-bearing, or the tier id no longer exists on the linked calibre.
  const tierName = useMemo(
    () =>
      (hit: WatchReferenceAlgoliaHit): string | null => {
        if (!hit.calibreTierId || !hit.calibre) return null;
        const tierMap = tierNamesByCalibre.get(normalizeCalibreKey(hit.calibre));
        return tierMap?.get(hit.calibreTierId) ?? null;
      },
    [tierNamesByCalibre],
  );

  // Render one result row. `viaDescription` marks rows that matched only through
  // the description text (not the exact calibre facet) with a distinguishing chip.
  const renderRow = (hit: WatchReferenceAlgoliaHit, viaDescription: boolean) => (
    <button
      key={hit.objectID}
      type="button"
      onClick={() => openReference(hit)}
      disabled={openingId === hit.objectID}
      className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-gray-50 disabled:opacity-50"
    >
      <RefThumb hit={hit} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-brand-700 bg-brand-50 rounded px-1.5 py-0.5 shrink-0">
            {brandName(hit.brandId)}
          </span>
          <span className="font-medium text-gray-900 truncate">{hit.reference}</span>
          {tierName(hit) && (
            <span className="text-xs font-medium text-indigo-700 bg-indigo-50 rounded px-1.5 py-0.5 shrink-0">
              Tier: {tierName(hit)}
            </span>
          )}
          {viaDescription && (
            <span
              className="text-xs font-medium text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 shrink-0"
              title="Matched via the description, not the calibre field"
            >
              via description
            </span>
          )}
        </div>
        <div className="text-sm text-gray-600 truncate">
          {hit.model || hit.productName || '-'}
          {hit.collection ? (
            <span className="text-gray-400"> · {hit.collection}</span>
          ) : null}
        </div>
      </div>
      <div className="text-xs text-gray-500 truncate max-w-[220px] hidden sm:block">
        {hit.calibre}
      </div>
      {openingId === hit.objectID && (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600 shrink-0" />
      )}
    </button>
  );

  // ── Editor view ───────────────────────────────────────────────────────────────
  if (editorTarget) {
    return (
      <div className="space-y-4">
        <nav className="flex items-center gap-2 text-sm">
          <button
            onClick={() => {
              const guard = editorExitGuardRef.current;
              if (guard) guard(() => setEditorTarget(null));
              else setEditorTarget(null);
            }}
            className="text-brand-600 hover:underline"
          >
            Calibre Search
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-700">{brandName(editorTarget.brand.id)}</span>
          <span className="text-gray-400">/</span>
          <span className="text-gray-700">{editorTarget.reference.reference}</span>
        </nav>
        <ReferenceEditor
          key={editorTarget.reference.id}
          reference={editorTarget.reference}
          brand={editorTarget.brand}
          exitGuardRef={editorExitGuardRef}
          onBack={() => setEditorTarget(null)}
          onSaved={(updated) => {
            // Reflect the saved edits in the list row without a re-query.
            const applyEdit = (h: WatchReferenceAlgoliaHit) =>
              h.referenceId === updated.id
                ? {
                    ...h,
                    calibre: updated.calibre ?? h.calibre,
                    model: updated.model ?? h.model,
                    // Take the tier verbatim (may be cleared) so the chip tracks edits.
                    calibreTierId: updated.calibreTierId,
                  }
                : h;
            setHits((prev) => prev.map(applyEdit));
            setDescriptionHits((prev) => prev.map(applyEdit));
            setEditorTarget(null);
          }}
          onDeleted={() => {
            setHits((prev) => prev.filter((h) => h.referenceId !== editorTarget.reference.id));
            setDescriptionHits((prev) =>
              prev.filter((h) => h.referenceId !== editorTarget.reference.id),
            );
            setTotalHits((t) => Math.max(0, t - 1));
            setEditorTarget(null);
          }}
        />
      </div>
    );
  }

  // ── Search view ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Calibre Search</h1>
        <p className="text-gray-500 mt-1">
          Find every reference using a specific calibre, across all brands. Pick a
          calibre from the list — start typing to narrow it (e.g. “Elaboré”).
        </p>
      </div>

      {!configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Search is not configured. Set up Algolia to use cross-brand calibre search.
        </div>
      )}

      {/* Calibre picker */}
      <div className="max-w-xl">
        <label className="block text-sm font-medium text-gray-700 mb-1">Calibre</label>
        <div className="relative">
          <input
            type="text"
            value={query}
            disabled={!configured}
            onChange={(e) => {
              setQuery(e.target.value);
              // Typing after a selection re-opens the picker for a new choice.
              if (selectedCalibre) setSelectedCalibre(null);
            }}
            onFocus={() => configured && setPickerOpen(true)}
            placeholder="Search calibres (e.g. Elaboré, ETA 2824, 7S26)…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-9 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
          />
          {query && (
            <button
              type="button"
              onClick={clearCalibre}
              aria-label="Clear calibre"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
            >
              ×
            </button>
          )}
          {pickerOpen && (
            <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
              {pickerLoading && options.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-400">Searching…</li>
              ) : options.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-400">No matching calibres</li>
              ) : (
                <>
                  {options.slice(0, PICKER_DISPLAY_CAP).map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        onClick={() => selectCalibre(opt)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-3"
                      >
                        <span className="text-sm text-gray-900 truncate">{opt.value}</span>
                        <span className="text-xs text-gray-400 tabular-nums shrink-0">
                          {opt.count.toLocaleString()}
                        </span>
                      </button>
                    </li>
                  ))}
                  {options.length > PICKER_DISPLAY_CAP && (
                    <li className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-100">
                      Showing top {PICKER_DISPLAY_CAP} of{' '}
                      {options.length.toLocaleString()} matching — refine your
                      search to see more
                    </li>
                  )}
                </>
              )}
            </ul>
          )}
        </div>
        {/* Click-away backdrop to close the picker */}
        {pickerOpen && (
          <div
            className="fixed inset-0 z-0"
            onClick={() => setPickerOpen(false)}
            aria-hidden
          />
        )}

        {/* Also-search-descriptions toggle */}
        <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={alsoSearchDescriptions}
            onChange={(e) => setAlsoSearchDescriptions(e.target.checked)}
            disabled={!configured}
          />
          <span>
            Also search descriptions
            <span className="block text-xs text-gray-400">
              Include refs whose description mentions the calibre — grade/variant
              info (e.g. “Elaboré grade”) often lives only there.
            </span>
          </span>
        </label>
      </div>

      {/* Matching references */}
      {selectedCalibre && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium text-gray-900">
              {totalHits.toLocaleString()}
            </span>
            reference{totalHits !== 1 ? 's' : ''} using
            <span className="font-medium text-gray-900">“{selectedCalibre.value}”</span>
            {alsoSearchDescriptions && descriptionHits.length > 0 && (
              <span className="text-gray-400">
                · +{descriptionHits.length.toLocaleString()}
                {descriptionTruncated ? '+' : ''} via description
              </span>
            )}
          </div>

          {listLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
            </div>
          ) : hits.length === 0 && descriptionHits.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              {descriptionLoading ? 'Searching descriptions…' : 'No references found.'}
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg divide-y divide-gray-100 overflow-hidden">
              {hits.map((hit) => renderRow(hit, false))}
              {descriptionHits.map((hit) => renderRow(hit, true))}
            </div>
          )}

          {descriptionTruncated && !listLoading && (
            <p className="text-xs text-gray-400 text-center">
              Showing the first {DESCRIPTION_MATCH_CAP.toLocaleString()} description
              matches — refine the calibre to narrow them.
            </p>
          )}

          {hasMore && !listLoading && (
            <div className="flex justify-center pt-2">
              <Button variant="secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
